import React, { useEffect } from 'react';
import { graphql, StaticQuery } from 'gatsby';
import { useInView } from 'react-intersection-observer';
import { EnquiryForm } from '../forms';
import {
  Wrapper,
  HtmlContent,
  Image,
} from '../common';
import { convertColorToHex } from '../../util/helpers';
import etchedStars from '../../assets/gifs/ETCH_STARS.gif';
import './EnquiryFormBlock.scss';

const getSlug = (location) => {
  if (!location) return null;
  const {
    pathname = '',
  } = location;
  return pathname ? pathname.replace(/\//g, '') : '';
};

const EnquiryFormBlockTemplate = (props) => {
  const {
    data,
    isHome,
    settings,
    etchedPomTwo,
    etchedPomThree,
    location,
  } = props;
  const {
    phone,
    email,
  } = settings;
  const {
    primary,
  } = data;
  const {
    sectionTheme,
    formTitle,
    formDescription,
  } = primary;
  const formAffix = getSlug(location);
  const sectionHex = sectionTheme ? convertColorToHex(sectionTheme) : null;
  let ref = null;
  let inView = null;
  if (isHome) {
    const [inViewRef, inViewBool] = useInView({
      /* Optional options */
      threshold: 0.2,
    });
    ref = inViewRef;
    inView = inViewBool;
    useEffect(() => {
      if (inView) document.documentElement.style.setProperty('--bg-colour', sectionHex);
    }, [inView]);
  }
  return (
    <section
      className="enquiry-form-block"
      ref={ref}
    >
      <Wrapper isBound>
        <div className="illustration illustration-stars">
          <img src={etchedStars} alt="A cluster of stars" />
        </div>
        <h2 className="title">{formTitle.text}</h2>
        <div className="cols">
          <div className="col col-left">
            <HtmlContent className="description" content={formDescription.html} />
            <div className="details email-details">
              <h3 className="details-title">Email us</h3>
              <a
                className="email"
                href={`mailto:${email.text}`}
              >
                {email.text}
              </a>
            </div>
            <div className="details phone-details">
              <h3 className="details-title">Give us a call</h3>
              <a
                className="telephone"
                href={`tel:${phone.text}`}
              >
                {phone.text}
              </a>
            </div>
          </div>
          <div className="col col-right">
            <EnquiryForm
              formName={`enquiry-form${formAffix ? `-${formAffix}` : ''}`}
            />
          </div>
        </div>
        <div className="illustration-group">
          <div className="illustration illustration-hand">
            <svg viewBox="0 0 223 220" xmlns="http://www.w3.org/2000/svg">
              <g fill="none" fillRule="evenodd">
                <path
                  d="M165.96 102.582c16.877-10.996 27.568-17.27 32.076-18.821 6.76-2.328 18.07-2.515 19.322 1.123 1.253 3.638 6.234 11.256 5.093 14.789-1.142 3.532.558 17.135-21.138 33.73-21.696 16.594-27.056 31.273-33.586 40.695-6.531 9.422-18.166 33.28-36.37 39.35-18.202 6.07-16.05 4.883-35.199 6.482-19.148 1.6-32.08-20.014-41.733-30.757C44.77 178.43 4.498 117.28 2.499 105.187c-2-12.092-3.817-17.873 2.2-19.945 6.016-2.072 27.644 13.466 29.999 15.242 1.57 1.184 6.441 4.067 14.614 8.65-23.888-29.615-36.462-46.25-37.721-49.908-1.889-5.486-10.503-16.866-.953-23.482 9.55-6.615 42.175 23.161 48.735 30.672a4183.805 4183.805 0 0017.279 19.622C66.235 50.921 60.09 30.641 58.216 25.198c-2.81-8.162 4.06-23.636 17.851-15.613C89.86 17.61 97.982 39.2 107.347 51.58c6.243 8.253 10.422 15.451 12.537 21.594 4.281-33.201 7.284-51.742 9.009-55.624C131.48 11.728 135.963-1 145.9 1.17c9.938 2.17 18.013 15.914 18.34 31.151.327 15.237.315 39.77-.183 46.518-.333 4.498.302 12.413 1.903 23.743z"
                  fill="#F68E00"
                />
                <path
                  d="M198.283 98.132c-.734 1.771 1.276 3.705 2.237 6.496.96 2.79.567 8.18 4.618 6.574 4.052-1.606 10.753-7.58 9.434-11.413-1.32-3.832-3.149-8.176-6.413-7.052-3.265 1.124-9.142 3.624-9.876 5.395zM140.427 28.36c3.55-.113 5.854-.352 6.913-.717 3.528-1.214 4.316-3.149 4.815-3.32 1.086-.375-1.917-12.499-5.343-12.586-3.427-.087-8.873-.07-7.992 7.53.587 5.066 1.123 8.098 1.607 9.094zM72.239 14.467c1.913 1.332 3.446 3.67 4.597 7.013 1.727 5.014 2.364 6.865.413 7.537-1.952.672-5.96 4.262-7.994 2.753-2.035-1.51-2.975-9.626-1.94-13.378.691-2.502 2.332-3.81 4.924-3.925zM14.194 44.617c6.267-4.876 10.232-.502 11.118 2.071.886 2.574 4.1 5.469.348 8.152-3.752 2.684-4.67 2.479-7.8.424-3.13-2.054-9.933-5.771-3.666-10.647zM8.212 96.015c.564-.03 3.82-1.656 4.548-1.566 3.213.396 4.895 3.848 5.315 9.264.514 6.644-3.09 6.438-4.793 7.024-1.703.586-3.448-2.611-4.794-6.52-1.345-3.907-3.325-8.042-.276-8.202z"
                  fill="#EEEA00"
                />
              </g>
            </svg>
          </div>
          <div className="illustration illustration-pom-two">
            <Image
              image={{ localFile: etchedPomTwo }}
              className="contain"
            />
          </div>
          <div className="illustration illustration-pom-three">
            <Image
              image={{ localFile: etchedPomThree }}
              className="contain"
            />
          </div>
        </div>
      </Wrapper>
    </section>
  );
};

const EnquiryFormBlock = props => (
  <StaticQuery
    query={graphql`
      query ContactFormQuery {
        settings: prismicSettings {
          data {
            email {
              text
            }
            phone {
              text
            }
          }
        }
        etchedPomTwo: file(relativePath: { eq: "assets/images/etched/ETCH_POM_TWO.PNG" }) {
          childImageSharp {
            fluid(maxWidth: 300 quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        etchedPomThree: file(relativePath: { eq: "assets/images/etched/ETCH_POM_THREE.PNG" }) {
          childImageSharp {
            fluid(maxWidth: 300 quality: 90) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }
      }
    `}
    render={data => (
      <EnquiryFormBlockTemplate
        {...props}
        etchedPomTwo={data.etchedPomTwo}
        etchedPomThree={data.etchedPomThree}
        settings={data.settings.data}
      />
    )}
  />
);

export default EnquiryFormBlock;
